import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box, CircularProgress, Typography, Tabs, Tab,
    Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
} from '@mui/material';
import { Search, Warning, CalendarMonth, Close as CloseIcon } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { Tooltip } from '@mui/material';
import { getInvoiceSchedule } from '../../../Redux/actions';
import { selectFocusedStyle, inputLabelFocusedStyle } from '../../../theme/formStyles';
import { exportInvoiceScheduleToExcel } from '../../../utils/Common';

// ─── Dark navy / teal palette ─────────────────────────────────────────────────
const G = {
    hdr1:       '#1a2535',  // darkest – group headers / summary strip / footer
    hdr1alt:    '#243040',  // alternating month group header
    hdr2:       '#263545',  // sub-column headers
    hdr2alt:    '#2d3e50',  // alternating sub-column headers
    totalsHdr:  '#2d4a3e',  // totals column header
    highlight:  '#4dd0e1',  // invoiced / collected amounts in summary
    upcoming:   '#81c784',
    rowTotals:  '#f5fcf9',
    paidBg:     '#f1fff1',
    actualBg:   '#f8fbff',
    plannedBg:  '#fffdf5',
    invBadgeBg: '#e3f2fd',
    invBadge:   '#1565c0',
    primary:    '#008080',
    primaryHov: '#006666',
};
// ─────────────────────────────────────────────────────────────────────────────

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const fmtAmt = (v) => (v == null || v === 0) ? '' : INR.format(v);
const fmtDate = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

const STATUS_COLORS = {
    invoiced:           { bg: '#e8f5e9', color: '#2e7d32', label: 'INVOICED'   },
    partially_invoiced: { bg: '#fff3e0', color: '#e65100', label: 'PARTIAL'    },
    open:               { bg: '#e3f2fd', color: '#1565c0', label: 'OPEN'       },
    draft:              { bg: '#f5f5f5', color: '#757575', label: 'DRAFT'      },
    confirmed:          { bg: '#e8f5e9', color: '#2e7d32', label: 'CONFIRMED'  },
    overdue:            { bg: '#fce4ec', color: '#b71c1c', label: 'OVERDUE'    },
};

const BILLING_CYCLES = ['Monthly', 'Quarterly', 'Half Yearly', 'Yearly', 'One Time'];
const STATUSES = [
    { value: 'invoiced',           label: 'Invoiced'           },
    { value: 'partially_invoiced', label: 'Partially Invoiced' },
    { value: 'open',               label: 'Open'               },
    { value: 'draft',              label: 'Draft'              },
    { value: 'confirmed',          label: 'Confirmed'          },
    { value: 'overdue',            label: 'Overdue'            },
];

// ─── Table layout constants ───────────────────────────────────────────────────
const COL_WIDTHS = [140, 180, 130, 90, 80, 90, 90, 90, 120];
const LEFT_OFFSETS = COL_WIDTHS.reduce((acc, w, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + COL_WIDTHS[i - 1]);
    return acc;
}, []);
const TOTAL_LEFT_WIDTH = COL_WIDTHS.reduce((s, w) => s + w, 0);
const ROW1_H = 32; // height of the first thead row in px
const PLANNED_W = 90;
const ACTUAL_W  = 130;
const COLLECT_W = 90;
const MONTH_W   = PLANNED_W + ACTUAL_W + COLLECT_W;

const thBase = {
    padding: '6px 8px',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(255,255,255,0.1)',
    background: G.hdr1,
    color: '#fff',
};

const tdBase = {
    padding: '5px 8px',
    fontSize: '12px',
    border: '1px solid #eeeeee',
    verticalAlign: 'top',
    background: '#fff',
};

const LAST_STICKY = COL_WIDTHS.length - 1;
const stickyDivider = { boxShadow: '3px 0 6px -2px rgba(0,0,0,0.18)' };

// sticky-left + sticky-top (row 2 corner cells)
const stickyTh = (i) => ({
    ...thBase,
    position: 'sticky',
    left: LEFT_OFFSETS[i],
    top: ROW1_H,
    minWidth: COL_WIDTHS[i],
    maxWidth: COL_WIDTHS[i],
    zIndex: 4,
    ...(i === LAST_STICKY ? stickyDivider : {}),
});

// sticky-left only (body cells)
const stickyTd = (i, bg = '#fff') => ({
    ...tdBase,
    position: 'sticky',
    left: LEFT_OFFSETS[i],
    minWidth: COL_WIDTHS[i],
    maxWidth: COL_WIDTHS[i],
    background: bg,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    zIndex: 2,
    ...(i === LAST_STICKY ? stickyDivider : {}),
});

// ─── Sub-components ───────────────────────────────────────────────────────────

function SummaryStrip({ summary }) {
    const { totalSOs, totalPlanned, totalInvoiced, totalCollected,
        invoicedPercent, collectionPercent, overdueSlots, upcomingSlots } = summary;

    const item = (label, value, valueColor = '#fff') => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, borderRight: '1px solid rgba(255,255,255,0.15)' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: valueColor, mt: 0.2 }}>
                {value}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', background: G.hdr1, borderRadius: 1, mb: 1.5, py: 1, px: 1 }}>
            {item('SOS', totalSOs)}
            {item('Total Planned', fmtAmt(totalPlanned) || '₹0')}
            {item('Invoiced', fmtAmt(totalInvoiced) || '₹0', G.highlight)}
            {item('Collected', fmtAmt(totalCollected) || '₹0', G.highlight)}
            {item('Invoiced %', `${invoicedPercent}%`)}
            {item('Collection %', `${collectionPercent}%`)}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Warning sx={{ fontSize: 12, color: '#ff8a65' }} /> Overdue Slots
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: overdueSlots > 0 ? '#ff8a65' : '#fff', mt: 0.2 }}>
                    {overdueSlots}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2 }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <CalendarMonth sx={{ fontSize: 12, color: G.upcoming }} /> Upcoming
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: G.upcoming, mt: 0.2 }}>
                    {upcomingSlots}
                </Typography>
            </Box>
        </Box>
    );
}

function StatusChip({ status }) {
    const cfg = STATUS_COLORS[status?.toLowerCase()] || { bg: '#f5f5f5', color: '#555', label: status?.toUpperCase() || '—' };
    return (
        <Chip label={cfg.label} size="small"
            sx={{ fontWeight: 700, fontSize: '10px', bgcolor: cfg.bg, color: cfg.color, height: 20, borderRadius: '4px' }}
        />
    );
}

function MonthCell({ slot }) {
    if (!slot.planned && !slot.actual) return null;
    if (!slot.planned) return null;
    return (
        <Box sx={{ fontSize: '11px', fontWeight: 600, color: slot.notRaised ? '#c62828' : '#555', textAlign: 'right' }}>
            {fmtAmt(slot.planned)}
            {slot.notRaised && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: 'flex-end', mt: 0.2 }}>
                    <Warning sx={{ fontSize: 10, color: '#c62828' }} />
                    <Typography sx={{ fontSize: '10px', color: '#c62828', fontWeight: 700 }}>Not raised</Typography>
                </Box>
            )}
        </Box>
    );
}

function ActualCell({ slot }) {
    if (!slot.actual) return null;
    const statusColor = { paid: G.primary, overdue: '#c62828', sent: '#1565c0' }[slot.paymentStatus] || '#333';
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: statusColor, textAlign: 'right' }}>
                {fmtAmt(slot.actual)}
            </Typography>
            {slot.invoiceNumbers.map((inv, i) => (
                <Typography key={i} sx={{
                    fontSize: '10px', color: G.invBadge, fontWeight: 600,
                    background: G.invBadgeBg, borderRadius: '3px',
                    px: 0.5, py: 0.1, display: 'inline-block', textAlign: 'center', whiteSpace: 'nowrap',
                }}>
                    {inv}
                </Typography>
            ))}
        </Box>
    );
}

function CollectedCell({ slot }) {
    if (!slot.collected) return null;
    return (
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: G.primary, textAlign: 'right' }}>
            {fmtAmt(slot.collected)}
        </Typography>
    );
}

function ScheduleTable({ rows, months }) {
    const row1Ref = useRef(null);
    const [row1H, setRow1H] = useState(ROW1_H);

    useEffect(() => {
        if (row1Ref.current) {
            setRow1H(row1Ref.current.getBoundingClientRect().height);
        }
    }, [rows, months]);

    if (rows.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="textSecondary">No data found for the selected filters.</Typography>
            </Box>
        );
    }

    const colTotals = months.map((_, mi) => ({
        planned:   rows.reduce((s, r) => s + r.months[mi].planned, 0),
        actual:    rows.reduce((s, r) => s + r.months[mi].actual, 0),
        collected: rows.reduce((s, r) => s + r.months[mi].collected, 0),
    }));
    const grandPlanned   = rows.reduce((s, r) => s + r.totalPlanned, 0);
    const grandActual    = rows.reduce((s, r) => s + r.totalActual, 0);
    const grandCollected = rows.reduce((s, r) => s + r.totalCollected, 0);

    const LEFT_HDR_COLS = ['Client Group', 'Company / Facility', 'SO / PO No', 'Cycle', 'Basis', 'Status', 'SO Start', 'SO End', 'Renewal Date'];

    return (
        <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 340px)', border: `1px solid ${G.hdr2}`, borderRadius: 1 }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: TOTAL_LEFT_WIDTH + months.length * MONTH_W + 310 }}>
                <colgroup>
                    {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
                    {months.flatMap((_, mi) => [
                        <col key={`p${mi}`} style={{ width: PLANNED_W }} />,
                        <col key={`a${mi}`} style={{ width: ACTUAL_W }} />,
                        <col key={`c${mi}`} style={{ width: COLLECT_W }} />,
                    ])}
                    <col style={{ width: 100 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 100 }} />
                </colgroup>

                <thead>
                    {/* Row 1: group headers — sticky top */}
                    <tr ref={row1Ref}>
                        <th colSpan={9} style={{ ...thBase, textAlign: 'left', position: 'sticky', top: 0, left: 0, zIndex: 5, ...stickyDivider }}>
                            Invoice Schedule
                        </th>
                        {months.map((mo, mi) => (
                            <th key={mi} colSpan={3} style={{ ...thBase, textAlign: 'center', background: mi % 2 === 0 ? G.hdr1 : G.hdr1alt, position: 'sticky', top: 0, zIndex: 3 }}>
                                {mo}
                            </th>
                        ))}
                        <th colSpan={3} style={{ ...thBase, textAlign: 'center', background: G.totalsHdr, position: 'sticky', top: 0, zIndex: 3 }}>Totals</th>
                    </tr>
                    {/* Row 2: sub-column headers — sticky top + sticky left for first 9 */}
                    <tr>
                        {LEFT_HDR_COLS.map((label, i) => (
                            <th key={i} style={{ ...stickyTh(i), top: row1H }}>{label}</th>
                        ))}
                        {months.flatMap((_, mi) => {
                            const bg = mi % 2 === 0 ? G.hdr2 : G.hdr2alt;
                            return [
                                <th key={`p${mi}`} style={{ ...thBase, textAlign: 'right', fontSize: '10px', background: bg, position: 'sticky', top: row1H, zIndex: 3 }}>Planned ₹</th>,
                                <th key={`a${mi}`} style={{ ...thBase, textAlign: 'right', fontSize: '10px', background: bg, position: 'sticky', top: row1H, zIndex: 3 }}>Actual ₹ + Inv#</th>,
                                <th key={`c${mi}`} style={{ ...thBase, textAlign: 'right', fontSize: '10px', background: bg, position: 'sticky', top: row1H, zIndex: 3 }}>Collected ₹</th>,
                            ];
                        })}
                        <th style={{ ...thBase, textAlign: 'right', background: G.totalsHdr, position: 'sticky', top: row1H, zIndex: 3 }}>Total Planned</th>
                        <th style={{ ...thBase, textAlign: 'right', background: G.totalsHdr, position: 'sticky', top: row1H, zIndex: 3 }}>Total Actual</th>
                        <th style={{ ...thBase, textAlign: 'right', background: G.totalsHdr, position: 'sticky', top: row1H, zIndex: 3 }}>Total Collected</th>
                    </tr>
                </thead>

                <tbody>
                    {rows.map((row, ri) => {
                        const rowBg = ri % 2 === 0 ? '#fff' : '#fafafa';
                        return (
                            <tr key={ri}>
                                <td style={stickyTd(0, rowBg)}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#222', lineHeight: 1.3 }}>
                                        {row.client_group}
                                    </Typography>
                                </td>
                                <td style={stickyTd(1, rowBg)}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#333', lineHeight: 1.3 }}>
                                        {row.company_name}
                                    </Typography>
                                    {row.facility && (
                                        <Typography sx={{ fontSize: '10px', color: '#888', mt: 0.2 }}>
                                            {row.facility}
                                        </Typography>
                                    )}
                                </td>
                                <td style={stickyTd(2, rowBg)}>
                                    <Typography sx={{ fontSize: '11px', color: '#444', fontWeight: 500 }}>
                                        {row.so_no}
                                    </Typography>
                                </td>
                                <td style={stickyTd(3, rowBg)}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#555', textTransform: 'uppercase' }}>
                                        {row.cycle || '—'}
                                    </Typography>
                                </td>
                                <td style={stickyTd(4, rowBg)}>
                                    <Typography sx={{ fontSize: '10px', color: '#666' }}>{row.basis || 'Prepaid'}</Typography>
                                </td>
                                <td style={stickyTd(5, rowBg)}>
                                    <StatusChip status={row.status} />
                                </td>
                                <td style={stickyTd(6, rowBg)}>
                                    <Typography sx={{ fontSize: '10px', color: '#555' }}>{fmtDate(row.so_start)}</Typography>
                                </td>
                                <td style={stickyTd(7, rowBg)}>
                                    <Typography sx={{ fontSize: '10px', color: '#555' }}>{fmtDate(row.so_end)}</Typography>
                                </td>
                                <td style={stickyTd(8, rowBg)}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: G.primary }}>
                                        {fmtDate(row.renewal_date)}
                                    </Typography>
                                </td>

                                {row.months.map((slot, mi) => {
                                    const cellBg = slot.notRaised       ? '#fff8f8'
                                        : slot.paymentStatus === 'paid' ? G.paidBg
                                        : slot.actual > 0               ? G.actualBg
                                        : slot.planned > 0              ? G.plannedBg
                                        : rowBg;
                                    return [
                                        <td key={`p${mi}`} style={{ ...tdBase, background: cellBg, textAlign: 'right' }}>
                                            <MonthCell slot={slot} />
                                        </td>,
                                        <td key={`a${mi}`} style={{ ...tdBase, background: cellBg, textAlign: 'right' }}>
                                            <ActualCell slot={slot} />
                                        </td>,
                                        <td key={`c${mi}`} style={{ ...tdBase, background: cellBg, textAlign: 'right' }}>
                                            <CollectedCell slot={slot} />
                                        </td>,
                                    ];
                                })}

                                <td style={{ ...tdBase, textAlign: 'right', background: G.rowTotals, fontWeight: 700, fontSize: '11px' }}>
                                    {fmtAmt(row.totalPlanned)}
                                </td>
                                <td style={{ ...tdBase, textAlign: 'right', background: G.rowTotals, fontWeight: 700, fontSize: '11px', color: G.primary }}>
                                    {fmtAmt(row.totalActual)}
                                </td>
                                <td style={{ ...tdBase, textAlign: 'right', background: G.rowTotals, fontWeight: 700, fontSize: '11px', color: G.primary }}>
                                    {fmtAmt(row.totalCollected)}
                                </td>
                            </tr>
                        );
                    })}

                </tbody>

                {/* Footer totals row — sticky bottom + sticky left for first 9 cols */}
                <tfoot>
                    <tr>
                        {COL_WIDTHS.map((w, i) => (
                            <td key={i} style={{
                                ...tdBase,
                                background: G.hdr1,
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '12px',
                                position: 'sticky',
                                left: LEFT_OFFSETS[i],
                                bottom: 0,
                                minWidth: w,
                                maxWidth: w,
                                zIndex: 4,
                                ...(i === LAST_STICKY ? stickyDivider : {}),
                            }}>
                                {i === 0 ? `TOTAL (${rows.length} SOs)` : ''}
                            </td>
                        ))}
                        {colTotals.flatMap((ct, mi) => [
                            <td key={`p${mi}`} style={{ ...tdBase, background: G.hdr1alt, textAlign: 'right', fontWeight: 700, fontSize: '11px', color: '#fff', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                {fmtAmt(ct.planned)}
                            </td>,
                            <td key={`a${mi}`} style={{ ...tdBase, background: G.hdr1alt, textAlign: 'right', fontWeight: 700, fontSize: '11px', color: G.highlight, position: 'sticky', bottom: 0, zIndex: 1 }}>
                                {fmtAmt(ct.actual)}
                            </td>,
                            <td key={`c${mi}`} style={{ ...tdBase, background: G.hdr1alt, textAlign: 'right', fontWeight: 700, fontSize: '11px', color: G.upcoming, position: 'sticky', bottom: 0, zIndex: 1 }}>
                                {fmtAmt(ct.collected)}
                            </td>,
                        ])}
                        <td style={{ ...tdBase, background: G.totalsHdr, textAlign: 'right', fontWeight: 700, fontSize: '11px', color: '#fff', position: 'sticky', bottom: 0, zIndex: 1 }}>
                            {fmtAmt(grandPlanned)}
                        </td>
                        <td style={{ ...tdBase, background: G.totalsHdr, textAlign: 'right', fontWeight: 700, fontSize: '11px', color: G.highlight, position: 'sticky', bottom: 0, zIndex: 1 }}>
                            {fmtAmt(grandActual)}
                        </td>
                        <td style={{ ...tdBase, background: G.totalsHdr, textAlign: 'right', fontWeight: 700, fontSize: '11px', color: G.upcoming, position: 'sticky', bottom: 0, zIndex: 1 }}>
                            {fmtAmt(grandCollected)}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </Box>
    );
}

function buildFyOptions() {
    const today = new Date();
    const currentFyYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const options = [];
    for (let y = currentFyYear - 2; y <= currentFyYear + 1; y++) {
        options.push({ value: y, label: `FY ${y}-${String(y + 1).slice(2)}` });
    }
    return { options, currentFyYear };
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function InvoiceSchedule() {
    const dispatch = useDispatch();
    const { options: fyOptions, currentFyYear } = buildFyOptions();

    const [fyYear, setFyYear]     = useState(currentFyYear);
    const [subTab, setSubTab]     = useState(0);
    const [loading, setLoading]   = useState(true);
    const [scheduleData, setScheduleData] = useState(null);

    const [search, setSearch]       = useState('');
    const [cycle,  setCycle]        = useState('');
    const [status, setStatus]       = useState('');
    const [downloading, setDownloading] = useState(false);

    const fetchData = (params) => {
        setLoading(true);
        dispatch(getInvoiceSchedule(params)).then((resp) => {
            if (resp?.data?.success) setScheduleData(resp.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    useEffect(() => {
        fetchData({
            fy: fyYear,
            search:       search  || undefined,
            billingCycle: cycle   || undefined,
            status:       status  || undefined,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fyYear, search, cycle, status]);

    const activeTabData  = subTab === 0 ? scheduleData?.subscription_ocms : scheduleData?.sales_software;

    const handleDownload = () => {
        if (!scheduleData || !activeTabData) return;
        setDownloading(true);
        try {
            const label = subTab === 0 ? 'Subscription & OCMS' : 'Sales & Software Subscription';
            exportInvoiceScheduleToExcel(scheduleData.months, activeTabData.rows || [], label, fyYear);
        } finally {
            setDownloading(false);
        }
    };
    const subOcmsCount   = scheduleData?.subscription_ocms?.summary?.totalSOs ?? 0;
    const salesSwCount   = scheduleData?.sales_software?.summary?.totalSOs ?? 0;

    const tabSx = {
        '& .MuiTab-root': { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', minHeight: 36, color: '#666' },
        '& .Mui-selected': { color: `${G.primary} !important` },
        '& .MuiTabs-indicator': { backgroundColor: G.primary, height: '2px' },
    };

    return (
        <Box>
            {/* ── Top bar: tabs + legend + download ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, borderBottom: `2px solid ${G.primary}26` }}>
                <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ ...tabSx }}>
                    <Tab label={`Subscription & OCMS (${subOcmsCount})`} />
                    <Tab label={`Sales & Software Subscription (${salesSwCount})`} />
                </Tabs>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    {[
                        { color: G.primary,  bg: G.invBadgeBg, label: 'Invoiced'   },
                        { color: '#aaa',     bg: G.plannedBg,  label: 'Planned'    },
                        { color: '#c62828',  bg: '#fff8f8',    label: 'Not Raised' },
                        { color: G.primary,  bg: G.paidBg,     label: 'Paid'       },
                        { color: '#c62828',  bg: '#fff',       label: 'Overdue Inv'},
                        { color: '#1565c0',  bg: '#fff',       label: 'Sent'       },
                    ].map(leg => (
                        <Box key={leg.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, bgcolor: leg.bg, border: `1.5px solid ${leg.color}`, borderRadius: '2px' }} />
                            <Typography sx={{ fontSize: '10px', color: '#666' }}>{leg.label}</Typography>
                        </Box>
                    ))}
                    <Tooltip title="Download Invoice Schedule">
                        <span>
                            <IconButton onClick={handleDownload} disabled={downloading || loading || !scheduleData}>
                                {downloading ? <CircularProgress size={20} sx={{ color: G.primary }} /> : <DownloadIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* ── Filters ── */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                        value={fyYear}
                        onChange={(e) => setFyYear(e.target.value)}
                        sx={{ ...selectFocusedStyle, fontWeight: 700, color: G.primary }}
                        MenuProps={{ disableScrollLock: true }}
                    >
                        {fyOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '13px' }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    width: 220, px: 1.25, py: '6px',
                    border: '1px solid rgba(0,0,0,0.23)', borderRadius: '4px', bgcolor: '#fff',
                    '&:focus-within': { border: '1px solid #008080' },
                }}>
                    <Search sx={{ fontSize: 18, color: '#9ca3af', flexShrink: 0 }} />
                    <input
                        placeholder="Client / SO / Facility..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, minWidth: 0, background: 'transparent' }}
                    />
                    {search && (
                        <IconButton size="small" onClick={() => setSearch('')} sx={{ p: '2px', flexShrink: 0, color: '#9ca3af', '&:hover': { color: '#555' } }}>
                            <CloseIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    )}
                </Box>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={inputLabelFocusedStyle}>All Billing Cycles</InputLabel>
                    <Select
                        value={cycle}
                        label="All Billing Cycles"
                        onChange={(e) => setCycle(e.target.value)}
                        sx={selectFocusedStyle}
                        MenuProps={{ disableScrollLock: true }}
                    >
                        <MenuItem value="">All Billing Cycles</MenuItem>
                        {BILLING_CYCLES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={inputLabelFocusedStyle}>All Statuses</InputLabel>
                    <Select
                        value={status}
                        label="All Statuses"
                        onChange={(e) => setStatus(e.target.value)}
                        sx={selectFocusedStyle}
                        MenuProps={{ disableScrollLock: true }}
                    >
                        <MenuItem value="">All Statuses</MenuItem>
                        {STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                    </Select>
                </FormControl>
                </Box>
            </Box>

            {/* ── Summary strip ── */}
            {activeTabData && <SummaryStrip summary={activeTabData.summary} />}

            {/* ── Table ── */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <CircularProgress sx={{ color: G.primary }} />
                </Box>
            ) : !scheduleData ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="error">Failed to load invoice schedule.</Typography>
                </Box>
            ) : (
                <ScheduleTable rows={activeTabData?.rows || []} months={scheduleData.months} />
            )}
        </Box>
    );
}
